import { Params } from '@angular/router';
import { Stage } from '../models/common.models';

import { languages, params } from '@app/configurations';
import { environment } from '@env/environment';
import { ExplorerHash, ExplorerPaths } from '../models/main-menu.model';
import { IsPortalApp, IsSignupApp } from '../services/utilities.service';
import { fromEntries, joinObject } from './object.utilities';
import { objectCamelCaseToSnake } from './string.utilities';

export function getRawQueryParams(): Params {
  return fromEntries(new URLSearchParams(location.search) as any) as Params;
}

export function getQueryParameter(name: string): string | null {
  if (!name) {
    return null;
  } else {
    const url = window.location.href;
    name = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(url);

    if (!results) {
      return null;
    }
    if (!results[2]) {
      return '';
    }
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }
}

export function getQueryParametersAsString(): string {
  const params: string[] = [];
  const tmp = getRawQueryParams();

  Object.keys(tmp).forEach((key) => params.push(`${key}=${tmp[key]}`));

  return params.join('&');
}

export const getCurrentStage = (): Stage | '' => {
  if (location.origin === 'http://dashboard-local.adspert.net:8088') return 'local';
  const cookie = document.cookie;
  let cookieValue = '';
  if (cookie && cookie !== '') {
    const cookies = cookie.split(';');
    for (let i = 0; i < cookies.length; i += 1) {
      const t = cookies[i].trim();
      if (t.substring(0, 6) === 'stage=') {
        cookieValue = decodeURIComponent(t.substring(6));
        break;
      }
    }
    return cookieValue as Stage;
  } else {
    return '';
  }
};

export function isProductionStage(): boolean {
  return getCurrentStage() === 'production';
}

export function GetPageUrl(val, url = 'monitor') {
  url = `/${url}?`;
  Object.keys(val).forEach((key) => {
    const value = val[key];
    if (!!value) url += `${key}=${value}&`;
  });
  return url.slice(0, -1);
}

export function sentryTracePropogationURL() {
  const stage = getCurrentStage();
  let fragment = '';

  if (!!['beta', 'staging', 'development'].includes(stage)) fragment = '-' + stage.toLowerCase();

  return `https://dashboard${fragment}.adspert.net`;
}

export function encodeSearchTerms(d) {
  return d; //encodeURIComponent(d)
}

export function generateExplorerHref(path: ExplorerPaths, filters: ExplorerHash) {
  if (!path) return null;
  if (filters?.[params.ADSPERT_ID]) {
    filters.adspertAccountId = filters[params.ADSPERT_ID];
    delete filters[params.ADSPERT_ID];
  }
  if (path === 'forecast-quality-apollo') {
    return `${environment.app.explorerAppUrl}${path}?${new URLSearchParams(<Params>filters).toString()}`;
  }

  const hash = filters ? `#${encodeURIComponent(JSON.stringify({ filter: objectCamelCaseToSnake(filters) }))}` : '';

  return `${environment.app.explorerAppUrl}${path}${hash}`;
}

export function hasValidBillingKey(): boolean {
  const key = decodeURIComponent(location.href.split('billed-usage-report/')[1]);
  const trimmedKey = key
    .trim()
    .replace(/\s/g, '')
    .replace(/\r?\n|\r/g, '');
  if (trimmedKey !== key) {
    location.replace(`${location.origin}/billed-usage-report/${trimmedKey}`);
    return true;
  }
  return false;
}

export function GetOAuthPath(param: string, platform: 'google-mc' | 'amazon-sp') {
  const newParams = new URLSearchParams();
  const urlParts = location.pathname.split('/');

  urlParts.forEach((term, index) => {
    if (term.toLowerCase() === platform) newParams.append(param, urlParts[index + 1]);
  });

  return `${location.pathname}?${newParams.toString()}`;
}

export function sanitizeNullQueryParams(): string[] | null {
  const tmp = location.search
    .slice(1)
    .split('&')
    .filter((d) => !!d);

  const filteredParams = tmp
    .filter((d) => {
      const value = d.split('=')[1]?.toLowerCase();
      return value !== 'undefined' && value !== 'null';
    })
    .filter((d) => {
      // Remove invalid campaign/ad group params unless they are there
      if (d.startsWith(params.GLOBAL_FILTER_CAMPAIGN_ID) || d.startsWith(params.GLOBAL_FILTER_AD_GROUPS_ID)) {
        return d.split(':').length === 2;
      }
      return true;
    });

  return tmp.length > filteredParams.length ? filteredParams : null;
}
export const detectBaseHref = (): string => {
  switch (true) {
    case IsSignupApp():
      return '/signup/';
    case IsPortalApp():
      return '/portal/';
    default:
      return '/';
  }
};

export const ReplacePageParams = (p: {}): void => {
  location.replace(location.href.split('?')[0] + '?' + joinObject(p, '=', '&'));
};

export function getRedirectQueryParam(): string | null {
  const param = getQueryParameter('redirect');
  if (!param || typeof param !== 'string') return null;

  const value = decodeURIComponent(param);

  // redirect param must be a safe, relative path
  if (
    value.startsWith('/') && // must be relative
    !value.startsWith('//') && // not protocol-relative
    !/[\s'"]*javascript:/i.test(value) && // no JS injection
    !value.startsWith('data:') // no data URI
  ) {
    return value;
  }
  return null;
}
const getBrowserLang = (): string => {
  let browserLang = navigator.language || (navigator as any)['userLanguage'] || 'en';
  browserLang = browserLang.split('-')[0].toLowerCase();
  return browserLang;
};

export function setPortalParams(): void {
  const browserLang = getBrowserLang();
  const token = getQueryParameter('token');
  const redirectURL = getRedirectQueryParam();
  const currentLang = (getQueryParameter('lang') || '')?.toLowerCase();
  const supportedLangs = Object.values(languages).map((l) => String(l).toLowerCase());

  const defaultLang = supportedLangs.includes(currentLang)
    ? currentLang
    : supportedLangs.includes(browserLang)
      ? browserLang
      : 'en';

  const searchParams = new URLSearchParams({ lang: defaultLang });
  if (redirectURL) searchParams.set('redirect', redirectURL);
  if (token) searchParams.set('token', token);

  // Only push if different to current query
  const newQuery = `?${searchParams.toString()}`;
  if (window.location.search !== newQuery) {
    window.history.replaceState(null, '', newQuery);
  }
}
